import { Locale } from '../locale';
import { SquidexAuth } from './auth';

export interface ISquidexTextLabel {
  key: {
    iv: string;
  };
  text: {
    fr: string;
    en: string;
  };
}
export class SquidexTextLabels {
  public items: any[] = [];
  private static storageKey = 'uiLabels';
  async initialize() {
    if (!(await this.getStoredKeysFromStorage(SquidexTextLabels.storageKey))) {
      await this.getTextLabels(SquidexTextLabels.storageKey);
    }
  }
  async getTextLabels(storageKey: string): Promise<any> {
    const accessToken = await new SquidexAuth().getToken();
    if (!accessToken) {
      throw new Error('Failed to get access token');
    }
    const dataResponse = await fetch(`https://cloud.squidex.io/api/content/tel-jeunes/labels`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const resp = await dataResponse.json();
    if (resp) {
      if (resp.items && resp.items.length > 0) {
        const items = resp.items.map((item: { data?: ISquidexTextLabel }) => item?.data);
        if (items) {
          this.storeKeysWithExpiry(storageKey, items, 24 * 60 * 60); // 1 day expiration

          //this.storeKeysWithExpiry(storageKey, items, 0); // Disable cache

          throw new Error('Response did not return any items');
        }
      }
    } else {
      throw new Error('There was a problem with the response');
    }
  }
  storeKeysWithExpiry(storageKey: string, labels: ISquidexTextLabel[], ttlInSeconds: number) {
    const now = new Date();
    const item = {
      value: labels,
      expiry: now.getTime() + ttlInSeconds * 1000, // Convert seconds to milliseconds
    };
    localStorage.setItem(storageKey, JSON.stringify(item));
  }
  async getStoredKeysFromStorage(storageKey: string) {
    const storedData = localStorage.getItem(storageKey);
    if (!storedData) {
      return null;
    } else {
      const now = new Date();
      const item = JSON.parse(storedData);

      if (now.getTime() > item.expiry) {
        // Item has expired, trigger refresh
        await this.getTextLabels(SquidexTextLabels.storageKey);
      } else {
        return item.value;
      }
    }
  }

  // Check for a hardcoded key and return its value if valid.
  public static checkForHardCodedKey(textKey: string): string | null {
    const key = Locale.getKey(textKey);
    return key && key !== textKey ? key : null;
  }

  // Retrieve the Squidex label for a given text key from local storage,
  // with a fallback to checking hardcoded keys.
  public static getKey(textKey: string): string {
    const lang = Locale.language;
    const labelsJson = localStorage.getItem(this.storageKey);

    if (!labelsJson) {
      return this.checkForHardCodedKey(textKey) || ' ';
    }

    try {
      const parsedLabels: ISquidexTextLabel[] = JSON.parse(labelsJson).value;

      if (Array.isArray(parsedLabels) && parsedLabels.length > 0) {
        const matchedLabel = parsedLabels.find((label) => label.key.iv === textKey);

        // Return the label's text for the current language if available.
        if (matchedLabel && (lang === 'fr' || lang === 'en')) {
          return matchedLabel.text[lang];
        }
      }
    } catch {
      // Bad data?
      console.error('Failed to parse labels from local storage.');
    }

    // Fallback to hardcoded key or empty string if no match found.
    return this.checkForHardCodedKey(textKey) || ' ';
  }
}
